import {
  DATA_CONTROLS,
  type IntentPolicyDefinition,
} from "@/api/mock-data/investment-banking/intentPolicies";
import {
  type AgentBusinessUnit,
  type DataControl,
  type DataEnforcement,
  type EffectiveEnforcement,
  type EffectivePolicySummary,
  type EnforcementAgentRef,
  type EnforcementContext,
  type EnforcementCoverage,
  type EnforcementFinding,
  type EnforcementHop,
  type EnforcementStatus,
  type EnforcementTargetSummary,
  type EstateAgentMcpService,
  type McpEnforcement,
  policyToolKey,
  type ToolEnforcement,
} from "@/api/temp/AgentEstateService";

/**
 * Effective enforcement for an Intent Policy, derived - never stored.
 *
 * Every view of an Intent Policy (Agent, Group, Policy, Access Path) is this one
 * function over one mapping and the agents' own relationships. Nothing here
 * reads the store, so the numbers a recommendation card promises, the review
 * lists and the drawer shows cannot drift apart.
 */

export interface EnforcementAgentInput {
  id: string;
  name: string;
  businessUnit: AgentBusinessUnit;
  demoKey?: string;
  /** The policy is assigned to this agent. */
  applied: boolean;
  /** `${dimension}:${value}` for every grouping lens, i.e. `AgentEstateGroup.key`. */
  groupKeys: string[];
  mcpServices: EstateAgentMcpService[];
  /**
   * Targets whose native policy is not deployed yet (or failed to deploy):
   * `serverId`, `serverId::toolId` or `sourceId:controlId`. An assignment is
   * not enforcement until the policy carrying it is actually deployed.
   */
  undeployed?: ReadonlySet<string>;
}

export const undeployedTargetKey = {
  server: (serverId: string) => serverId,
  tool: policyToolKey,
  control: (sourceId: string, controlId: string) => `${sourceId}:${controlId}`,
};

interface AccessPath {
  id: string;
  serverId: string;
  sourceId?: string;
  toolIds: string[];
}

/**
 * One path per server and data source it reaches. The Access Flow branches use
 * the same ids, which is what lets a flow hop open its own enforcement.
 */
export function accessPathsFor(
  agentId: string,
  services: EstateAgentMcpService[],
): AccessPath[] {
  return services.flatMap(service => {
    const bySource = new Map<string, AccessPath>();
    service.tools.forEach(tool => {
      const sourceKey = tool.dataSourceId ?? "none";
      const path = bySource.get(sourceKey) ?? {
        id: `${agentId}:${service.id}:${sourceKey}`,
        serverId: service.id,
        sourceId: tool.dataSourceId,
        toolIds: [],
      };
      path.toolIds.push(tool.id);
      bySource.set(sourceKey, path);
    });
    return [...bySource.values()];
  });
}

/** How the statuses of one resource's parts combine into the resource's own. */
export function rollup(statuses: EnforcementStatus[]): EnforcementStatus {
  if (!statuses.length) return "NOT_MAPPED";
  if (statuses.includes("CONFLICT_DETECTED")) return "CONFLICT_DETECTED";
  if (statuses.every(status => status === "ENFORCED")) return "ENFORCED";
  if (statuses.every(status => status === "NOT_MAPPED")) return "NOT_MAPPED";
  if (
    statuses.every(
      status => status === "NOT_ENFORCED" || status === "NOT_MAPPED",
    )
  )
    return "NOT_ENFORCED";
  return "PARTIALLY_ENFORCED";
}

/** The same control seen by several agents: agreement keeps its status. */
function acrossAgents(statuses: EnforcementStatus[]): EnforcementStatus {
  if (statuses.includes("CONFLICT_DETECTED")) return "CONFLICT_DETECTED";
  return statuses.every(status => status === statuses[0])
    ? statuses[0]
    : "PARTIALLY_ENFORCED";
}

interface AgentEvaluation {
  agent: EnforcementAgentInput;
  servers: McpEnforcement[];
  sources: DataEnforcement[];
  status: EnforcementStatus;
}

function evaluateAgent(
  policy: IntentPolicyDefinition,
  agent: EnforcementAgentInput,
  active: boolean,
  path?: AccessPath,
): AgentEvaluation {
  const inactive = (
    status: EnforcementStatus,
    targetKey: string,
  ): EnforcementStatus =>
    active && !agent.undeployed?.has(targetKey) ? status : "NOT_ENFORCED";

  const services = path
    ? agent.mcpServices.filter(service => service.id === path.serverId)
    : agent.mcpServices;

  const servers = services.map((service): McpEnforcement => {
    const mapping = policy.mcp[service.id];
    const tools = service.tools
      .filter(tool => !path || path.toolIds.includes(tool.id))
      .map((tool): ToolEnforcement => {
        const override = mapping?.toolOverrides[tool.name];
        return {
          id: tool.id,
          name: tool.name,
          classification: tool.classification,
          mode: override ? "custom" : "inherit",
          rule: override?.rule ?? mapping?.serverPolicyName ?? "",
          status: mapping
            ? inactive(
                override?.status ?? "ENFORCED",
                undeployedTargetKey.tool(service.id, tool.id),
              )
            : "NOT_MAPPED",
        };
      });
    const serverPolicy = {
      name: mapping?.serverPolicyName ?? "",
      status: mapping
        ? inactive("ENFORCED", undeployedTargetKey.server(service.id))
        : ("NOT_MAPPED" as const),
    };
    return {
      serverId: service.id,
      name: service.name,
      technology: service.technology,
      serverPolicy,
      tools,
      customToolCount: tools.filter(tool => tool.mode === "custom").length,
      status: rollup([serverPolicy.status, ...tools.map(tool => tool.status)]),
      agentIds: [agent.id],
      registryServerId: mapping?.registryServerId,
    };
  });

  const sourceTools = services
    .flatMap(service => service.tools)
    .filter(tool => !path || path.toolIds.includes(tool.id));
  const sourceIds = [
    ...new Set(sourceTools.flatMap(tool => tool.dataSourceId ?? [])),
  ];
  const sources = sourceIds.map((sourceId): DataEnforcement => {
    const tool = sourceTools.find(item => item.dataSourceId === sourceId)!;
    const mapping = policy.data[sourceId];
    const controls = (mapping?.controlIds ?? []).map((id): DataControl => {
      const exception = mapping?.exceptions?.find(
        item => item.controlId === id && item.agentKey === agent.demoKey,
      );
      return {
        id,
        name: DATA_CONTROLS[id]?.name ?? id,
        description: DATA_CONTROLS[id]?.description ?? "",
        status: exception
          ? exception.status
          : inactive("ENFORCED", undeployedTargetKey.control(sourceId, id)),
        detail: exception?.detail,
      };
    });
    return {
      sourceId,
      name: tool.dataSourceName ?? tool.dataAccess,
      platform: tool.dataSourcePlatform ?? "",
      platformId: tool.dataSourcePlatformId,
      controls,
      status: rollup(controls.map(control => control.status)),
      agentIds: [agent.id],
    };
  });

  return {
    agent,
    servers,
    sources,
    status: rollup([
      ...servers.map(server => server.status),
      ...sources.map(source => source.status),
    ]),
  };
}

/** Fold per-agent views of the same server into one, tool by tool. */
function mergeServers(evaluations: AgentEvaluation[]): McpEnforcement[] {
  const merged = new Map<string, McpEnforcement[]>();
  evaluations.forEach(evaluation =>
    evaluation.servers.forEach(server =>
      merged.set(server.serverId, [
        ...(merged.get(server.serverId) ?? []),
        server,
      ]),
    ),
  );
  return [...merged.values()].map(views => {
    const [first] = views;
    const tools = first.tools.map(tool => ({
      ...tool,
      status: acrossAgents(
        views.map(
          view => view.tools.find(item => item.name === tool.name)!.status,
        ),
      ),
    }));
    const serverPolicy = {
      ...first.serverPolicy,
      status: acrossAgents(views.map(view => view.serverPolicy.status)),
    };
    return {
      ...first,
      serverPolicy,
      tools,
      status: rollup([serverPolicy.status, ...tools.map(tool => tool.status)]),
      agentIds: views.flatMap(view => view.agentIds),
    };
  });
}

function mergeSources(evaluations: AgentEvaluation[]): DataEnforcement[] {
  const merged = new Map<string, DataEnforcement[]>();
  evaluations.forEach(evaluation =>
    evaluation.sources.forEach(source =>
      merged.set(source.sourceId, [
        ...(merged.get(source.sourceId) ?? []),
        source,
      ]),
    ),
  );
  return [...merged.values()].map(views => {
    const [first] = views;
    const controls = first.controls.map(control => {
      const statuses = views.map(
        view => view.controls.find(item => item.id === control.id)!,
      );
      return {
        ...control,
        status: acrossAgents(statuses.map(item => item.status)),
        detail: statuses.find(item => item.detail)?.detail,
      };
    });
    return {
      ...first,
      controls,
      status: rollup(controls.map(control => control.status)),
      agentIds: views.flatMap(view => view.agentIds),
    };
  });
}

function findingsFor(
  evaluations: AgentEvaluation[],
  applied: boolean,
): EnforcementFinding[] {
  // Before the policy is in effect everything is Not enforced; listing each
  // control as a finding would restate the headline status dozens of times.
  if (!applied) return [];
  const findings: EnforcementFinding[] = [];
  const add = (finding: Omit<EnforcementFinding, "agentNames">, name: string) => {
    const existing = findings.find(item => item.id === finding.id);
    if (existing) existing.agentNames.push(name);
    else findings.push({ ...finding, agentNames: [name] });
  };
  evaluations.forEach(({ agent, servers, sources }) => {
    servers
      .filter(server => server.serverPolicy.status !== "ENFORCED")
      .forEach(server =>
        add(
          {
            id: `server:${server.serverId}`,
            layer: "SERVER",
            status: server.serverPolicy.status,
            resource: server.name,
            control: server.serverPolicy.name,
          },
          agent.name,
        ),
      );
    sources.forEach(source => {
      if (!source.controls.length)
        add(
          {
            id: `source:${source.sourceId}`,
            layer: "DATA",
            status: "NOT_MAPPED",
            resource: source.name,
            control: "",
          },
          agent.name,
        );
      source.controls
        .filter(control => control.status !== "ENFORCED")
        .forEach(control =>
          add(
            {
              id: `control:${source.sourceId}:${control.id}`,
              layer: "DATA",
              status: control.status,
              resource: source.name,
              control: control.name,
              detail: control.detail,
            },
            agent.name,
          ),
        );
    });
  });
  return findings;
}

const coverageOf = (
  servers: McpEnforcement[],
  sources: DataEnforcement[],
): EnforcementCoverage => ({
  mcpServerCount: servers.length,
  toolCount: servers.reduce((count, server) => count + server.tools.length, 0),
  dataSourceCount: sources.length,
  controlCount: sources.reduce(
    (count, source) => count + source.controls.length,
    0,
  ),
});

function hopsFor(
  policy: IntentPolicyDefinition,
  evaluation: AgentEvaluation,
  applied: boolean,
): EnforcementHop[] {
  const server = evaluation.servers[0];
  const source = evaluation.sources[0];
  const tools = server?.tools ?? [];
  return [
    {
      layer: "AGENT",
      label: evaluation.agent.name,
      control: policy.name,
      status: applied ? "ENFORCED" : "NOT_ENFORCED",
    },
    {
      layer: "SERVER",
      label: server?.name ?? "",
      control: server?.serverPolicy.name ?? "",
      status: server?.serverPolicy.status ?? "NOT_MAPPED",
    },
    {
      layer: "TOOL",
      label: server?.name ?? "",
      control: `${tools.length - (server?.customToolCount ?? 0)} inherited · ${server?.customToolCount ?? 0} custom`,
      status: rollup(tools.map(tool => tool.status)),
    },
    {
      layer: "DATA",
      label: source?.name ?? "",
      control: (source?.controls ?? []).map(control => control.name).join(", "),
      status: source?.status ?? "NOT_MAPPED",
    },
  ];
}

interface EnforcementOptions {
  /**
   * Evaluate as though the policy were assigned to every candidate. What a
   * recommendation or review promises is this, before anything is applied.
   */
  assumeApplied?: boolean;
}

export function getEffectiveEnforcement(
  policy: IntentPolicyDefinition,
  context: EnforcementContext,
  agents: EnforcementAgentInput[],
  options: EnforcementOptions = {},
): EffectiveEnforcement | undefined {
  const candidates =
    context.type === "agent" || context.type === "accessPath"
      ? agents.filter(agent => agent.id === context.agentId)
      : context.type === "group"
        ? agents.filter(agent => agent.groupKeys.includes(context.groupId))
        : agents;
  if (!candidates.length) return undefined;

  const path =
    context.type === "accessPath"
      ? accessPathsFor(candidates[0].id, candidates[0].mcpServices).find(
          item => item.id === context.pathId,
        )
      : undefined;
  if (context.type === "accessPath" && !path) return undefined;

  const effective = options.assumeApplied
    ? candidates
    : candidates.filter(agent => agent.applied);
  const applied = effective.length > 0;
  const effectiveIds = new Set(effective.map(agent => agent.id));
  // Targets are listed even before the policy is in effect, so an agent that is
  // only recommended the policy still sees where it would be enforced.
  const evaluations = (applied ? effective : candidates).map(agent =>
    evaluateAgent(policy, agent, applied, path),
  );
  const mcpServers = mergeServers(evaluations);
  const dataSources = mergeSources(evaluations);
  const toRef = (evaluation: AgentEvaluation): EnforcementAgentRef => ({
    id: evaluation.agent.id,
    name: evaluation.agent.name,
    businessUnit: evaluation.agent.businessUnit,
    status: evaluation.status,
  });
  const single =
    context.type === "agent" || context.type === "accessPath"
      ? candidates[0]
      : undefined;

  return {
    policy: {
      id: policy.id,
      name: policy.name,
      description: policy.description,
      allow: policy.allow,
      restrict: policy.restrict,
    },
    context,
    contextLabel: single?.name ?? "",
    status: applied
      ? rollup([
          ...mcpServers.map(server => server.status),
          ...dataSources.map(source => source.status),
        ])
      : "NOT_ENFORCED",
    applied,
    agents: {
      effective: applied ? evaluations.map(toRef) : [],
      // Before anything is applied, `evaluations` already covers every
      // candidate unapplied; reuse it rather than evaluating twice.
      recommended: applied
        ? candidates
            .filter(agent => !effectiveIds.has(agent.id))
            .map(agent => toRef(evaluateAgent(policy, agent, false, path)))
        : evaluations.map(toRef),
    },
    coverage: coverageOf(mcpServers, dataSources),
    mcpServers,
    dataSources,
    path:
      path && evaluations[0]
        ? {
            id: path.id,
            label: [
              evaluations[0].agent.name,
              mcpServers[0]?.name,
              dataSources[0]?.name,
            ]
              .filter(Boolean)
              .join(" → "),
            hops: hopsFor(policy, evaluations[0], applied),
          }
        : undefined,
    findings: findingsFor(evaluations, applied),
  };
}

export const toTargetSummary = (
  enforcement: EffectiveEnforcement,
): EnforcementTargetSummary => ({
  ...enforcement.coverage,
  mcpServers: enforcement.mcpServers.map(server => ({
    id: server.serverId,
    name: server.name,
    toolCount: server.tools.length,
  })),
  dataSources: enforcement.dataSources.map(source => ({
    id: source.sourceId,
    name: source.name,
    controlCount: source.controls.length,
  })),
});

export const toPolicySummary = (
  enforcement: EffectiveEnforcement,
): EffectivePolicySummary => ({
  policyId: enforcement.policy.id,
  name: enforcement.policy.name,
  applied: enforcement.applied,
  allow: enforcement.policy.allow,
  restrict: enforcement.policy.restrict,
  status: enforcement.status,
  coverage: enforcement.coverage,
  effectiveAgentCount: enforcement.agents.effective.length,
});
